import React, {
  MutableRefObject,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import classes from '../PostSchedule.module.scss';
import classNames from 'classnames';
import { CenteredContentLayout } from '../../../../components/containers/CenteredContentLayout';
import { PostScheduleViewProps } from '../PostSchedule.view';
import {
  PostItemFragment,
  InputMaybe,
  PostOrderUpdateDirection,
  PostStatus,
  PostTiming,
  QueueContext,
  QueueFilterPostStatus,
  QueueFilterPostTiming,
  QueueSortOrder
} from '../../../../models/graphql';
import { SelectAllCheckboxes } from '../../../../components/actions/SelectAllCheckboxes';
import { PostScheduleFilterMenu } from '../PostScheduleFilterMenu';
import { useTopNavClasses } from '../../../../hooks/useTopNavClasees';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { Loader } from '../../../../components/containers/Loader';
import { DetailedStatsModal } from '../DetailedStatsModal';
import { CardDragLayer } from '../CardDragLayer';
import { PostScheduleHelpers } from '../postScheduleHelpers';
import { CardOrientationEnum, UseFormPostCardProps } from '../../../../components/cards/BasePostCard/BasePostCard.view';
import { DraggablePostCard } from './DraggablePostCard';
import { getScrollableParent } from '../../../../helpers/scrollableHelpers';
import { UseFormReturn } from 'react-hook-form/dist/types/form';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { BasePostCard } from '../../../../components/cards/BasePostCard';
import { WeekdayDayMonthYearFullFormat } from '../../../../constants/time-formats';
import { PostScheduleEmptyHeroBox } from '../PostScheduleEmptyHeroBox';
import { EmptyPlaceholder } from '../../../../components/containers/EmptyPlaceholder';
import pollingTimedOutHero from '../../../../assets/images/pollingTimedOut.svg';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import { useInView } from 'react-intersection-observer';
import { QueueCountContext } from '../../../../providers/QueueCountProvider';
import { usePauseSettings } from '../../../../hooks/usePauseSettings';
import { DuplicateOrReschedulePostHandler, PostScheduleViewContext } from '../PostSchedule.controller';
import { ScrollProvider } from '../../../../providers/ScrollProvider';
import { utcToZonedTime } from 'date-fns-tz';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

export type PostTimesType = {
  calculatedLocalDateTime: string;
};

export type PostScheduleListViewProps = PostScheduleViewProps & {
  pollingTimedOut: boolean;
  leftColRef: RefObject<HTMLDivElement>;
  rightColRef: RefObject<HTMLDivElement>;
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  posts: PostItemFragment[];
  getSubQueuePosts: (post: PostItemFragment) => void;
  hasReachedFetchLimit: boolean;
  fetchMorePostScheduleListItems: (sortOrder: QueueSortOrder, scrollPosition: 'TOP' | 'BOTTOM') => void;
  getPostsAfterPostMove: (post: PostItemFragment) => void;
  categoryFilterId: string;
  socialProfileId: string;
  postTimingFilter: InputMaybe<QueueFilterPostTiming> | undefined;
};

export type DraggablePostTypes =
  | {
      isDraggable: true;
      index: number;
      isPostCategoryPaused?: boolean;
      movePost: (dragId: string, hoverIndex: number | undefined) => void;
      cancelMove: () => void;
      endMove: () => void;
      animate: boolean;
      scrollToRef?: MutableRefObject<HTMLDivElement | null>;
    }
  | {
      isDraggable: false;
      isPostCategoryPaused?: boolean;
    };

export type PostItemHeaderProps =
  | {
      showDateHeader: true;
      dateTimeString: string;
    }
  | {
      showDateHeader: false;
    };

export type PostItemProps = PostItemHeaderProps &
  DraggablePostTypes & {
    post: PostItemFragment;
    postIndex: number;
    orientation: CardOrientationEnum;
    formInstance: UseFormReturn;
    menuListItems: ContainerListItem[];
    duplicateOrReschedulePost: DuplicateOrReschedulePostHandler;
    onVisible?: (isVisible: boolean, postId: string, index: number) => void;
    context?: QueueContext;
    isPostStatusUnqueued?: boolean;
  };

const getFormattedPostDateTime = (post: PostItemFragment, wsTz: string, status: QueueFilterPostStatus): string => {
  let dateTime: string;

  switch (post?.status) {
    case PostStatus.ACTIVE:
      if (
        post.calculatedUtcDatetime &&
        (status === QueueFilterPostStatus.SCHEDULED || status === QueueFilterPostStatus.PAST)
      ) {
        const calculatedInTz = utcToZonedTime(`${post.calculatedUtcDatetime}Z`, wsTz);
        dateTime = PostScheduleHelpers.getLocalDateFormatted(calculatedInTz, WeekdayDayMonthYearFullFormat);
        // dateTime = PostScheduleHelpers.getLocalDateFormatted(new Date(), WeekdayDayMonthYearFullFormat);
      } else {
        dateTime = PostScheduleHelpers.getLocalDateFormatted(new Date(post.dateAdded), WeekdayDayMonthYearFullFormat);
      }
      break;
    case PostStatus.PUBLISHED:
      dateTime = PostScheduleHelpers.getLocalDateFormatted(new Date(post.datePosted), WeekdayDayMonthYearFullFormat);
      break;
    case PostStatus.FAILED:
      dateTime = PostScheduleHelpers.getLocalDateFormatted(new Date(post.dateFailed), WeekdayDayMonthYearFullFormat);
      break;
    default:
      dateTime = PostScheduleHelpers.getLocalDateFormatted(new Date(post.dateAdded), WeekdayDayMonthYearFullFormat);
  }

  return dateTime;
};

export const PostItem = (props: PostItemProps): JSX.Element => {
  const {
    post,
    postIndex,
    orientation,
    onVisible,
    formInstance,
    showDateHeader,
    isDraggable,
    menuListItems,
    context,
    isPostStatusUnqueued,
    duplicateOrReschedulePost
  } = props;

  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;

  const [inViewRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.5
  });

  useEffect(() => {
    if (onVisible) {
      onVisible(inView, post.id, postIndex);
    }
  }, [inView, onVisible, post.id, postIndex]);

  const useFormPostCardProps: UseFormPostCardProps = !(showOnboarding && disableDelete)
    ? (post.status === PostStatus.ACTIVE || post.status === PostStatus.DRAFT) && context === QueueContext.SCHEDULE
      ? { hideCheckbox: false, useFormInstance: formInstance }
      : post.status !== PostStatus.FAILED && context !== QueueContext.LIBRARY
      ? { hideCheckbox: true }
      : { hideCheckbox: false, useFormInstance: formInstance }
    : { hideCheckbox: true };

  const openStatsAction = menuListItems.find(i => i.label === 'Detailed Stats')?.action ?? undefined;

  return (
    <>
      {showDateHeader && context !== QueueContext.LIBRARY && (
        <ListDateHeader
          dateTimeString={props.dateTimeString}
          postKey={`LIST-${props.dateTimeString}`}
          offsetCheckbox={!useFormPostCardProps.hideCheckbox ?? undefined}
          isFirstDateBlock={postIndex === 0}
        />
      )}
      {isDraggable && post.calculatedUtcDatetime ? (
        <div ref={props.scrollToRef ? props.scrollToRef : inViewRef}>
          <DraggablePostCard
            key={`draggable-post-${post.id}`}
            index={props.index}
            id={post.id}
            {...(showOnboarding && disableDelete ? {} : { useFormInstance: formInstance })}
            post={post}
            movePost={props.movePost}
            endMove={props.endMove}
            cancelMove={props.cancelMove}
            menuListItems={menuListItems}
            animate={props.animate}
          />
        </div>
      ) : (
        <div className={'margin-bottom-small-16'} key={`POST-LIST-${post.id}`} ref={inViewRef}>
          <BasePostCard
            context={PostScheduleViewContext.LIST}
            index={postIndex}
            orientation={orientation}
            post={post}
            menuListItems={menuListItems}
            hasHoverBackground={false}
            isDraggable={false}
            moveCard={undefined}
            reschedulePostHandler={duplicateOrReschedulePost}
            openDetailsModal={openStatsAction}
            {...(context === QueueContext.LIBRARY
              ? {
                  ...{
                    isPostStatusUnqueued
                  }
                }
              : {})}
            {...useFormPostCardProps}
          />
        </div>
      )}
    </>
  );
};

export const ListDateHeader = (props: {
  dateTimeString: string;
  postKey: string;
  offsetCheckbox?: boolean;
  hideDateString?: boolean;
  isFirstDateBlock?: boolean;
}): JSX.Element => {
  const { dateTimeString, postKey, offsetCheckbox, hideDateString, isFirstDateBlock } = props;

  const dateBlockLabelClasses = classNames(classes.DateBlockLabel, {
    [classes.DateBlockLabelFirstDateBlock]: isFirstDateBlock,
    [classes.DateBlockLabelWithCheckboxOffset]: offsetCheckbox
  });

  return (
    <div className={dateBlockLabelClasses} key={`date-block-${postKey}`}>
      {!hideDateString ? dateTimeString : ''}
    </div>
  );
};

export const PostScheduleListView = (props: PostScheduleListViewProps): JSX.Element => {
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;
  const { state: topNavOffsetState } = useContext(TopNavContext);
  const { loadingInitial, setLoadingInitial, setIsMovingPost, firstInViewId, handlePostVisibilityChange } =
    useContext(PostScheduleContext);
  const { queueCounts } = useContext(QueueCountContext);
  const { isSocialProfilePausedForCategory } = usePauseSettings();

  const hasSidebar = props.width >= BreakpointsValuesEnum.medium;

  const [grabbedPost, setGrabbedPost] = useState<PostItemFragment | undefined>(undefined);
  const [scrollPlacement, setScrollPlacement] = useState<'TOP' | 'BOTTOM' | undefined>(undefined);
  const [flashPostId, setFlashPostId] = useState<string | undefined>(undefined);
  const [cardTime, setCardTime] = useState<string>('');

  const scrollToRef = useRef<HTMLDivElement>(null);

  const topNavClasses = useTopNavClasses(topNavOffsetState);
  const leftColumnWrapperClass = classNames(classes.LeftSideWrapper, 'show-for-medium', topNavClasses.vheight);
  const leftColumnClass = classNames(classes.LeftSideColumn);
  const centerColumnClasses = classNames(classes.CentralContent, {
    [classes.CentralContentHasSideBar]: hasSidebar
  });

  const {
    pollingTimedOut,
    context,
    orientation,
    posts,
    formInstance,
    getActionMenuListItems,
    getPostsAfterPostMove,
    dispatchFilters,
    categoryFilterId,
    socialProfileId,
    postTimingFilter,
    loadingMore,
    updatePostLocation
  } = props;

  const { getSubQueuePosts, duplicateOrReschedulePost } = props;
  const refPostId = useMemo(() => grabbedPost?.id || -1, [grabbedPost]);

  const moveHandler = useCallback(
    (post: PostItemFragment) => {
      let querySubQueue = false;
      if (post.id !== refPostId) {
        setGrabbedPost(post);
        setIsMovingPost(true);

        // If we are already in the Cat and Profile, then don't get the sub queue
        if (post.socialProfile && post.socialProfile?.id !== socialProfileId) {
          dispatchFilters({ type: 'profile', payload: { data: post.socialProfile.id, fetchPosts: false } });
          querySubQueue = true;
        }

        if (post.category.id !== categoryFilterId) {
          dispatchFilters({ type: 'category', payload: { data: post.category.id, fetchPosts: false } });
          querySubQueue = true;
        }

        if (QueueFilterPostTiming.QUEUED !== postTimingFilter) {
          dispatchFilters({ type: 'timing', payload: { data: QueueFilterPostTiming.QUEUED, fetchPosts: false } });
          querySubQueue = true;
        }

        if (querySubQueue) {
          getSubQueuePosts(post);
        }
      }
    },
    [categoryFilterId, dispatchFilters, getSubQueuePosts, postTimingFilter, refPostId, setIsMovingPost, socialProfileId]
  );

  const [mutablePosts, setMutablePosts] = useState<PostItemFragment[]>([]);
  const [, setPostTimes] = useState<PostTimesType[]>([]);

  const movedPostCleanup = useCallback(() => {
    setGrabbedPost(undefined);
    setIsMovingPost(false);
  }, [setIsMovingPost]);

  const movePost = useCallback(
    (dragId: string, hoverOverIndex: number | undefined) => {
      setMutablePosts((prevPosts: PostItemFragment[]) => {
        const dragIndex = prevPosts.findIndex(post => post.id === dragId);
        const hoverIndex = hoverOverIndex ?? dragIndex;
        if (hoverIndex < 0 || hoverIndex >= prevPosts.length || dragIndex < 0) return prevPosts;

        const updatedPosts = [...prevPosts];
        const [draggedPost] = updatedPosts.splice(dragIndex, 1);
        updatedPosts.splice(hoverIndex, 0, draggedPost);

        setCardTime(updatedPosts[hoverIndex].calculatedLocalDateTime);
        setFlashPostId(undefined);
        moveHandler(draggedPost);

        return updatedPosts;
      });
    },
    [moveHandler]
  );

  const cancelMove = useCallback(() => {
    setMutablePosts(posts);
    movedPostCleanup();
  }, [movedPostCleanup, posts]);

  const endMove = useCallback(() => {
    if (!grabbedPost) return;

    // setLoadingInitial(true);

    const postIndex = mutablePosts.findIndex(post => post.id === grabbedPost.id);
    const isOnlyPost = postIndex === 0 && mutablePosts.length === 1;

    // If grabbedPost is not in mutablePosts, exit early
    // If there's only one post, we don't want to process further.
    if (isOnlyPost || postIndex === -1) {
      movedPostCleanup();
      return;
    }

    let targetPostId;
    let direction;

    const isFirstPost = postIndex === 0;

    if (isFirstPost) {
      targetPostId = mutablePosts[postIndex + 1].id;
      direction = PostOrderUpdateDirection.ABOVE;
    } else {
      targetPostId = mutablePosts[postIndex - 1].id;
      direction = PostOrderUpdateDirection.BELOW;
    }

    // we call movedPostCleanup after return success/error of updatePostLocation or if we don't enter
    if (targetPostId && direction) {
      updatePostLocation(grabbedPost.id, targetPostId, direction)
        .then(() => {
          setFlashPostId(grabbedPost.id);
          getPostsAfterPostMove(grabbedPost);
        })
        .finally(() => {
          movedPostCleanup();
          // setLoadingInitial(false);
        });
    } else {
      movedPostCleanup();
      setLoadingInitial(false);
    }
  }, [grabbedPost, setLoadingInitial, mutablePosts, movedPostCleanup, updatePostLocation, getPostsAfterPostMove]);

  const { queueModals, wsTz, queueFilters } = props;
  const statusFilter = queueFilters.status ?? QueueFilterPostStatus.SCHEDULED;

  const queryContext = PostScheduleHelpers.getQueryContext(statusFilter);

  const postItems = useMemo<JSX.Element | JSX.Element[]>(() => {
    const isDraggableListType =
      !mutablePosts.some(p => p.status !== PostStatus.ACTIVE) &&
      props.filters.status !== QueueFilterPostStatus.PAUSED &&
      props.filters.status !== QueueFilterPostStatus.UNQUEUED;

    let currentDateTimeString: string;

    return mutablePosts.map((post, index) => {
      let showDateHeader = false;
      const isPostStatusUnqueued = props.filters.status === QueueFilterPostStatus.UNQUEUED;
      const isDraggable =
        isDraggableListType && post.timing === PostTiming.QUEUE && orientation === CardOrientationEnum.LANDSCAPE;
      const dateTimeString = getFormattedPostDateTime(post, wsTz, statusFilter);

      if (!grabbedPost && dateTimeString !== currentDateTimeString) {
        showDateHeader = true;
        currentDateTimeString = dateTimeString;
      }

      const idsToCheck = [flashPostId, grabbedPost?.id, firstInViewId];
      const draggableProps: DraggablePostTypes = isDraggable
        ? {
            isDraggable,
            index,
            movePost,
            cancelMove,
            endMove,
            animate: post.id === flashPostId,
            scrollToRef: idsToCheck.includes(post.id) ? scrollToRef : undefined,
            isPostCategoryPaused: isSocialProfilePausedForCategory(post.socialProfile.id, post.category.id)
          }
        : {
            isDraggable,
            isPostCategoryPaused: isSocialProfilePausedForCategory(post.socialProfile.id, post.category.id)
          };

      const headerProps: PostItemHeaderProps = showDateHeader
        ? {
            showDateHeader,
            dateTimeString
          }
        : { showDateHeader };

      const postProps: PostItemProps = {
        ...draggableProps,
        ...headerProps,
        post,
        postIndex: index,
        orientation,
        formInstance,
        menuListItems: getActionMenuListItems(post),
        onVisible: handlePostVisibilityChange,
        duplicateOrReschedulePost,
        context: queryContext,
        isPostStatusUnqueued
      };

      return <PostItem key={`POST-${context}-${post.id}`} {...postProps} />;
    });
  }, [
    cancelMove,
    context,
    duplicateOrReschedulePost,
    endMove,
    firstInViewId,
    flashPostId,
    formInstance,
    getActionMenuListItems,
    grabbedPost,
    handlePostVisibilityChange,
    isSocialProfilePausedForCategory,
    movePost,
    mutablePosts,
    orientation,
    props.filters.status,
    queryContext,
    statusFilter,
    wsTz
  ]);

  // Used to invoke re-render
  useEffect(() => {
    setPostTimes(
      posts.map(p => {
        return { calculatedLocalDateTime: p['calculatedLocalDateTime'] };
      }) || []
    );
    setMutablePosts(posts);
  }, [posts]);

  useEffect(() => {
    if (scrollToRef.current && !loadingInitial) {
      setTimeout(() => {
        scrollToRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0); // we may need to adjust this delay
    }
  }, [scrollToRef, flashPostId, loadingInitial]);

  const formItemsContainerClasses = classNames('grid-x', classes.FormItemsContainerQueue);
  const postItemsWrapperClasses = classNames(classes.PostItemsWrapper, {
    [classes.PostItemsWrapperWithMore]: !props.hasReachedFetchLimit
  });
  const topScrollZoneClasses = classNames(classes.TopScrollZone);
  const bottomScrollZoneClasses = classNames(classes.BottomScrollZone);

  const topScrollRef = useRef<HTMLDivElement>(null);

  const scrollDiv = useCallback((direction?: 'UP' | 'DOWN') => {
    const scrollElement = getScrollableParent(topScrollRef.current);
    const pxValue = direction === 'UP' ? -50 : 50;
    scrollElement?.scrollBy({ top: pxValue });
  }, []);
  const selectAllContainer = classNames('cell auto', classes.SelectAllContainer);
  const loaderClasses = classNames(classes.QueueLoaderWrapper, {
    [classes.QueueLoaderWrapperWithSidebar]: hasSidebar
  });
  const loaderPositionClasses = classNames(classes.QueueLoaderPosition, {
    [classes.QueueLoaderPositionAdjusted]: hasSidebar
  });

  const { fetchMorePostScheduleListItems } = props;

  // We allow filter change and post manipulation, this ensures when filters change, we end grabbed post assignment
  useEffect(() => {
    setGrabbedPost(undefined);
    setIsMovingPost(false);
  }, [props.filters, setIsMovingPost]);

  return (
    <>
      {hasSidebar && (
        <div className={leftColumnWrapperClass}>
          <div className={leftColumnClass} ref={props.leftColRef}>
            <PostScheduleFilterMenu
              filters={props.filters}
              context={props.context}
              dispatchFilters={props.dispatchFilters}
              categories={props.categories}
              socialProfiles={props.socialProfiles}
              formInstance={props.formInstance}
              onSubmit={props.onSubmit}
              setSearchTerm={props.setSearchTerm}
              queueCounts={queueCounts}
              isLoading={loadingInitial}
            />
          </div>
        </div>
      )}
      <ScrollProvider
        className={centerColumnClasses}
        scrollToBottomHandler={() => {
          setScrollPlacement('BOTTOM');
          fetchMorePostScheduleListItems(QueueSortOrder.ASCENDING, 'BOTTOM');
        }}
        scrollToTopHandler={() => {
          setScrollPlacement('TOP');
          fetchMorePostScheduleListItems(QueueSortOrder.DESCENDING, 'TOP');
        }}>
        <CenteredContentLayout containerClasses={classes.ContainerClassCentralizedContent} hasSidebar={hasSidebar}>
          {!loadingInitial && (!!grabbedPost || loadingMore) && (
            <div ref={topScrollRef} className={topScrollZoneClasses}>
              {props.loadingMore && scrollPlacement === 'TOP' && (
                <Loader variation={'dark'} size={'medium'} type={'inline'} />
              )}
            </div>
          )}
          {loadingInitial && (
            <div className={loaderClasses}>
              <div className={loaderPositionClasses}>
                <Loader variation={'dark'} size={'large'} type={'inline'} />
              </div>
            </div>
          )}
          <div className={postItemsWrapperClasses} ref={props.rightColRef}>
            {mutablePosts.length > 0 &&
              !grabbedPost &&
              props.queueFilters.status !== QueueFilterPostStatus.PAST &&
              !(showOnboarding && disableDelete) && (
                <div className={formItemsContainerClasses}>
                  <div className={selectAllContainer}>
                    <SelectAllCheckboxes
                      formFieldName={'postItemCheckBox'}
                      formInstance={formInstance}
                      label={'Select All'}
                      size={'large'}
                    />
                  </div>
                </div>
              )}
            <div className={postItemsWrapperClasses}>
              {/* postItems can be empty as we wait for the mutation of mutablePosts, so check posts as well */}
              {pollingTimedOut ? (
                <EmptyPlaceholder title={'There was a problem with displaying your queue'} image={pollingTimedOutHero}>
                  <div>
                    It seems there&apos;s a problem with rendering your queue. Please allow a little more time while we
                    work to resolve this issue. If the problem persists, you can try reloading the page to see if that
                    helps.
                  </div>
                </EmptyPlaceholder>
              ) : Object.keys(postItems).length || props.posts.length || loadingInitial ? (
                postItems
              ) : (
                <PostScheduleEmptyHeroBox
                  status={PostScheduleHelpers.mapStringToStatusType(props.filters.status)}
                  context={props.context}
                  categoryIdFilter={props.filters.category}
                  socialProfileIdFilter={props.filters.profile}
                />
              )}
            </div>
          </div>
          {!loadingInitial && (!!grabbedPost || loadingMore) && (
            <div className={bottomScrollZoneClasses}>
              {props.loadingMore && scrollPlacement === 'BOTTOM' && (
                <Loader variation={'dark'} size={'medium'} type={'inline'} />
              )}
            </div>
          )}
        </CenteredContentLayout>
      </ScrollProvider>
      {queueModals.details.state && (
        <DetailedStatsModal
          posts={props.postIterations}
          onClose={() => queueModals.details.setter(false)}
          getActionMenuListItems={props.getActionMenuListItems}
          reschedulePostHandler={props.duplicateOrReschedulePost}
        />
      )}
      <CardDragLayer
        orientation={props.orientation}
        scheduleItems={mutablePosts}
        hasSidebar={true}
        cardTime={cardTime}
        scrollHandler={scrollDiv}
        isProfilePausedForCategory={
          grabbedPost
            ? isSocialProfilePausedForCategory(grabbedPost.socialProfile.id, grabbedPost.category.id) || false
            : false
        }
      />
    </>
  );
};
